import { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, X } from "lucide-react";
import { api } from "@/context/AuthContext";

const T = {
  en: { open: "Open Human Chat", title: "Human-to-Human Chat (Unlocked)", ph: "Type a message...", empty: "Channel unlocked. Say hi!" },
  es: { open: "Abrir Chat Humano", title: "Chat Humano a Humano (Desbloqueado)", ph: "Escribe un mensaje...", empty: "Canal desbloqueado. ¡Saluda!" },
};

export const MatchChat = ({ matchId, lang }) => {
  const t = T[lang] || T.en;
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const poll = () => api.get(`/matches/${matchId}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
    poll();
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [open, matchId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    await api.post(`/matches/${matchId}/messages`, { text: text.trim() });
    setText("");
    const { data } = await api.get(`/matches/${matchId}/messages`);
    setMsgs(data);
  };

  if (!open)
    return (
      <button data-testid="open-chat-btn" onClick={() => setOpen(true)}
        className="ml-3 px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#00F5FF]/10 border border-[#00F5FF]/40 text-[#00F5FF] inline-flex items-center gap-2">
        <MessageCircle size={13} /> {t.open}
      </button>
    );

  return (
    <div className="mt-4 bg-[#05050A]/60 border border-[#00F5FF]/25 rounded-2xl p-4" data-testid="match-chat-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#00F5FF] font-bold">{t.title}</p>
        <button onClick={() => setOpen(false)} data-testid="close-chat-btn" className="text-white/40 hover:text-white"><X size={15} /></button>
      </div>
      <div className="max-h-56 overflow-y-auto space-y-2 mb-3 pr-1">
        {msgs.length === 0 && <p className="text-[11px] text-white/30">{t.empty}</p>}
        {msgs.map((m) => (
          <div key={m._id} className={`text-xs p-2.5 rounded-xl max-w-[85%] ${m.mine ? "ml-auto bg-[#B026FF]/15 border border-[#B026FF]/25" : "bg-white/5 border border-white/10"}`} data-testid="chat-message">
            <span className="text-white/85">{m.text}</span>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t.ph}
          className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-xs focus:outline-none focus:border-[#00F5FF]/50" />
        <button data-testid="chat-send-btn" onClick={send}
          className="w-9 h-9 rounded-full bg-[#00F5FF]/15 border border-[#00F5FF]/40 text-[#00F5FF] flex items-center justify-center shrink-0">
          <Send size={14} />
        </button>
      </div>
    </div>
  );
};
